import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, X, Search, Mail } from 'lucide-react'
import api from '../../../../services/authService'
import { GrupoInstitucion, MiembroInstitucion, ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  isOpen: boolean
  onClose: () => void
  salon: GrupoInstitucion
  todosMiembros: MiembroInstitucion[]
  expedientesDelSalon: ExpedienteDespacho[]
}

export const ModalAlumnosSalon: React.FC<Props> = ({
  isOpen,
  onClose,
  salon,
  todosMiembros,
  expedientesDelSalon
}) => {
  const [alumnosGrupo, setAlumnosGrupo] = useState<any[]>([])
  const [cargando, setCargando] = useState(false)
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => {
    if (!isOpen || !salon) return

    const fetchAlumnos = async () => {
      setCargando(true)
      try {
        const res = await api.get(`/api/institucion/grupos/${salon.id_grupo}/miembros`)
        if (Array.isArray(res.data) && res.data.length > 0) {
          setAlumnosGrupo(res.data)
        } else {
          // Si no tiene miembros específicos asignados todavía, usamos los miembros institucionales
          setAlumnosGrupo(todosMiembros)
        }
      } catch (err) {
        // Fallback a miembros institucionales
        setAlumnosGrupo(todosMiembros)
      } finally {
        setCargando(false)
      }
    }

    fetchAlumnos()
  }, [isOpen, salon, todosMiembros])

  if (!isOpen) return null

  // Filtrado de alumnos
  const alumnosFiltrados = alumnosGrupo.filter((a) => {
    const q = busqueda.toLowerCase()
    const nombreCompleto = `${a.nombre || ''} ${a.apellido || ''}`.toLowerCase()
    const email = (a.email || '').toLowerCase()
    return nombreCompleto.includes(q) || email.includes(q)
  })

  // Obtener rol en los expedientes de este salón
  const obtenerRolEnExpedientes = (alumnoId: number | string, nombreAlumno: string) => {
    const roles: string[] = []
    expedientesDelSalon.forEach((exp) => {
      if (
        exp.actores.emisor.alumnoId === alumnoId ||
        exp.actores.emisor.alumnoNombre?.toLowerCase().includes(nombreAlumno.toLowerCase())
      ) {
        roles.push(`Emisor (${exp.folio})`)
      }
      if (
        exp.actores.agenteAduanal.alumnoId === alumnoId ||
        exp.actores.agenteAduanal.alumnoNombre?.toLowerCase().includes(nombreAlumno.toLowerCase())
      ) {
        roles.push(`Agente Aduanal (${exp.folio})`)
      }
      if (
        exp.actores.receptor.alumnoId === alumnoId ||
        exp.actores.receptor.alumnoNombre?.toLowerCase().includes(nombreAlumno.toLowerCase())
      ) {
        roles.push(`Receptor (${exp.folio})`)
      }
    })
    return roles
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden text-slate-100 max-h-[85vh] flex flex-col"
      >
        {/* Cabecera */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                  Salón / Aula
                </span>
                <span className="text-xs text-slate-400 font-medium">
                  {salon.descripcion || 'Grupo Institucional'}
                </span>
              </div>
              <h3 className="text-base font-black text-white">{salon.nombre}</h3>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Barra de Búsqueda */}
        <div className="p-4 border-b border-slate-800/80 bg-slate-950/40">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar alumno por nombre o correo..."
              className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700/80 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>

        {/* Lista de Alumnos */}
        <div className="p-5 overflow-y-auto space-y-2.5 flex-1">
          {cargando ? (
            <div className="py-12 text-center text-slate-400 text-xs">
              Cargando alumnos del salón...
            </div>
          ) : alumnosFiltrados.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-xs">
              No se encontraron alumnos para este salón.
            </div>
          ) : (
            alumnosFiltrados.map((alumno, idx) => {
              const nombre = `${alumno.nombre || ''} ${alumno.apellido || ''}`.trim() || 'Alumno'
              const rolesAsignados = obtenerRolEnExpedientes(
                alumno.id_usuario || alumno.id_miembro || idx,
                nombre
              )

              return (
                <div
                  key={alumno.id_miembro || alumno.id_usuario || idx}
                  className="p-3.5 rounded-2xl bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center font-bold text-cyan-400 text-xs uppercase">
                      {nombre.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-white text-xs">{nombre}</p>
                      <p className="text-[11px] text-slate-400 flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-500" />
                        {alumno.email || 'Sin correo registrado'}
                      </p>
                    </div>
                  </div>

                  {/* Badges de Roles Activos en este Salón */}
                  <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
                    {rolesAsignados.length > 0 ? (
                      rolesAsignados.map((rol, rIdx) => (
                        <span
                          key={rIdx}
                          className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/25"
                        >
                          {rol}
                        </span>
                      ))
                    ) : (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-800 text-slate-400 border border-slate-700/60">
                        Disponible para asignación
                      </span>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/60 flex items-center justify-between text-xs text-slate-400">
          <span>{alumnosFiltrados.length} alumnos mostrados</span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-white font-bold transition-colors"
          >
            Cerrar
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default ModalAlumnosSalon
